import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import {
  useLazyGetApiProgramsQuery,
  useLazyGetApiUsersQuery,
  useLazyGetApiLocationsQuery,
} from '../../../../app/athenaApiGen';
import { setFormData } from '../../../../app/projects/projectsSlice';

const FormGrid = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

export default function ProjectForm() {
  const dispatch = useDispatch();
  const formData = useSelector((state) => state.projects.formData);
  const errors = useSelector((state) => state.projects.errors);
  const modalMode = useSelector((state) => state.projects.modalMode);
  
  const [fetchPrograms, { data: programsData, isLoading: programsLoading }] = useLazyGetApiProgramsQuery();
  const [fetchUsers, { data: usersData, isLoading: usersLoading }] = useLazyGetApiUsersQuery();
  const [fetchLocations, { data: locationsData, isLoading: locationsLoading }] = useLazyGetApiLocationsQuery();

  // Fetch programs, users, and locations on mount
  React.useEffect(() => {
    fetchPrograms();
    fetchUsers();
    fetchLocations();
  }, [fetchPrograms, fetchUsers, fetchLocations]);

  // Debug: Log the API responses
  React.useEffect(() => {

  }, [programsData, usersData]);

  // API returns arrays directly, not wrapped in objects
  const programs = programsData || [];
  const users = usersData || [];
  const locations = locationsData || [];

  const handleFieldChange = (field, value) => {
    dispatch(setFormData({ [field]: value }));
  };

  return (
    <Grid container spacing={3}>
      {/* Project Title - Required */}
      <FormGrid size={{ xs: 12 }}>
        <FormLabel htmlFor="project-title" required>
          Project Title
        </FormLabel>
        <TextField
          id="project-title"
          name="title"
          placeholder="Network Infrastructure Upgrade"
          value={formData.title}
          onChange={(e) => handleFieldChange('title', e.target.value)}
          error={!!errors.title}
          helperText={errors.title}
          required
          size="small"
          fullWidth
        />
      </FormGrid>

      {/* Project Code - Required */}
      <FormGrid size={{ xs: 12, md: 6 }}>
        <FormLabel htmlFor="project-code" required>
          Project Code
        </FormLabel>
        <TextField
          id="project-code"
          name="projectCode"
          placeholder="PROJ-2025-001"
          value={formData.projectCode}
          onChange={(e) => handleFieldChange('projectCode', e.target.value)}
          error={!!errors.projectCode}
          helperText={errors.projectCode || (modalMode === 'edit' ? 'Project code cannot be changed' : '')}
          required
          size="small"
          fullWidth
          disabled={modalMode === 'edit'}
        />
      </FormGrid>

      {/* Program - Required */}
      <FormGrid size={{ xs: 12, md: 6 }}>
        <FormLabel htmlFor="program" required>
          Program
        </FormLabel>
        <TextField
          id="program"
          name="program"
          value={(() => {
            // Show loading state while programs are being fetched
            if (programsLoading) return 'Loading...';

            // If no programId is selected, show empty
            if (!formData.programId) return '';

            // Find the selected program
            const selectedProgram = programs.find(p => p.programId === formData.programId);

            // If programId exists but program not found yet (still loading), show loading
            if (!selectedProgram) return 'Loading...';

            // Display program name with contract code if available
            if (selectedProgram.contractCode) {
              return `${selectedProgram.title} (${selectedProgram.contractCode})`;
            }
            return selectedProgram.title || '';
          })()}
          disabled
          size="small"
          fullWidth
          placeholder="No program assigned"
        />
      </FormGrid>

      {/* Owner - Required */}
      <FormGrid size={{ xs: 12, md: 6 }}>
        <FormLabel htmlFor="owner" required>
          Project Owner
        </FormLabel>
        <Autocomplete
          id="owner"
          options={users}
          getOptionLabel={(option) => {
            // Display userName or contact name if available
            return option.userName || option.contact?.name || '';
          }}
          // Use user.id as the unique key for React (each user record is unique)
          getOptionKey={(option) => option.id}
          // For proper comparison when finding the selected value
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={users.find(u => u.id === formData.ownerId) || null}
          // Save the user.id, not contact.contactId - we'll map this in CardModal on submit
          onChange={(_, newValue) => handleFieldChange('ownerId', newValue?.id || '')}
          loading={usersLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select an owner"
              error={!!errors.ownerId}
              helperText={errors.ownerId}
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </FormGrid>

      {/* Location - Optional */}
      <FormGrid size={{ xs: 12, md: 6 }}>
        <FormLabel htmlFor="location">
          Location
        </FormLabel>
        <Autocomplete
          id="location"
          options={locations}
          getOptionLabel={(option) => {
            // Display location name or location code if available
            return option.name || option.locationCode || '';
          }}
          // Use locationId as the unique key for each option
          getOptionKey={(option) => option.locationId}
          // For proper comparison when finding the selected value
          isOptionEqualToValue={(option, value) => option.locationId === value.locationId}
          value={locations.find(l => l.locationId === formData.locationId) || null}
          onChange={(_, newValue) => handleFieldChange('locationId', newValue?.locationId || '')}
          loading={locationsLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select a location"
              error={!!errors.locationId}
              helperText={errors.locationId}
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {locationsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </FormGrid>

      {/* Project File Path - Optional */}
      <FormGrid size={{ xs: 12, md: 6 }}>
        <FormLabel htmlFor="project-file-path">
          Project File Path
        </FormLabel>
        <TextField
          id="project-file-path"
          name="projectFilePath"
          placeholder="/projects/2025/network-upgrade"
          value={formData.projectFilePath}
          onChange={(e) => handleFieldChange('projectFilePath', e.target.value)}
          size="small"
          fullWidth
        />
      </FormGrid>

      {/* Description - Optional */}
      <FormGrid size={{ xs: 12 }}>
        <FormLabel htmlFor="description">
          Description
        </FormLabel>
        <TextField
          id="description"
          name="description"
          placeholder="Upgrade network infrastructure for improved performance"
          value={formData.description}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          multiline
          rows={4}
          size="small"
          fullWidth
        />
      </FormGrid>
    </Grid>
  );
}